export interface GroceryItem {
  id: string;
  name: string;
  original: string; // Original recipe text like "2 cups milk"
  amount?: number;
  unit?: string;
  category: GroceryCategory;
  isChecked: boolean;
  isCustom: boolean; // True if user added manually
  recipeIds: string[]; // Which recipes need this ingredient
  recipeNames: string[]; // Recipe names for display

  // Smart quantity tracking (Phase 1)
  quantityInInventory?: number; // How many we already have
  totalNeeded?: number; // Total quantity needed from all recipes
  stillNeed?: number; // Calculated: totalNeeded - quantityInInventory

  // Phase 3: Unit mismatch flag
  unitMismatch?: boolean; // True if inventory has count but recipe uses weight/volume

  /**
   * The ingredient text behind this row is still in the recipe's source
   * language, because no full translation has been stored for it yet.
   *
   * The row is still real: it counts toward the totals and can be checked off.
   * Only the NAME is masked in the UI, because showing "medium yellow onion" to
   * a French user is worse than showing that something is on its way — and
   * hiding the row entirely would drop an ingredient the user has to buy.
   *
   * Derived at generation time from the contributing meals, never stored on the
   * recipe, so it cannot drift from the week snapshot hash. See
   * `pendingTranslationRecipeIds` in groceryListService.
   */
  pendingTranslation?: boolean;

  // Per-recipe quantity breakdown (when ingredient is in multiple recipes)
  recipeBreakdown?: Array<{ recipeId: string; recipeName: string; amount: number; unit: string }>;

  // Phase 2: Already-have tracking
  source?: GroceryItemSource; // How we know we have this item
  stapleKey?: string; // For source:'staple' — the StapleGroup.key

  // Batch tracking for undo (e.g. grocery toast "Annuler")
  batchId?: string;
}

export type GroceryItemSource = 'inventory' | 'manual-review' | 'both' | 'staple';

export enum GroceryCategory {
  Produce = 'produce',
  Dairy = 'dairy',
  Meat = 'meat',
  Bakery = 'bakery',
  Pantry = 'pantry',
  Spices = 'spices',
  Frozen = 'frozen',
  Beverages = 'beverages',
  Other = 'other',
}

export interface SkippedIngredient {
  name: string; // Ingredient name
  quantityInInventory: number; // How many we have
  unit?: string; // Unit of measurement
  category: GroceryCategory; // Category for grouping
  recipeNames: string[]; // Which recipes need this ingredient
  isExpiring?: boolean; // True if expiring soon
}

export interface WasteReductionMetrics {
  totalIngredients: number; // Total unique ingredients across all recipes
  alreadyHave: number; // Ingredients we have in inventory
  needToBuy: number; // Missing ingredients
  percentageSaved: number; // (alreadyHave / totalIngredients) * 100
  estimatedSavings: number; // Estimated cost savings in dollars
  expiringIngredientsUsed: number; // Count of expiring items being used
  skippedItems: SkippedIngredient[]; // List of ingredients already in inventory
}

export interface GroceryListState {
  items: GroceryItem[]; // All grocery items (missing ingredients + custom)
  alreadyHaveItems: GroceryItem[]; // Items covered by inventory or manual review
  metrics: WasteReductionMetrics | null;
  generatedDate: string | null; // ISO timestamp when list was generated
  weekSnapshot: string; // Hash of planned meal IDs to detect changes
  // Week (Monday, YYYY-MM-DD) this list belongs to. The list is pinned to one
  // week at a time: browsing other weeks in the planner must not rewrite it.
  // Only an explicit user action ("shop for this week") repoints it.
  sourceWeekStart: string | null;
  // Last day (YYYY-MM-DD, inclusive) the list also covers past the pinned
  // week's Sunday, or null for the pinned week alone. A plan runs seven days
  // from today, so a Thursday plan ends next Wednesday; without this the list
  // said "ready" and covered four dinners of seven (#722). Set only by an
  // explicit act — onboarding, or "add to the grocery list" after extending.
  coversUntil?: string | null;
  customItems: GroceryItem[]; // User-added custom items
  isStale: boolean; // True when meal plan has changed but list hasn't regenerated
  isRegenerating: boolean; // True during list regeneration
  syncLoading?: boolean; // True during cloud sync
  syncError?: string | null; // Error message from cloud sync
}

// Category display metadata
export interface CategoryMetadata {
  name: string;
  labelKey: string;
  icon: string;
  color: string;
  sortOrder: number;
}

export const CATEGORY_METADATA: Record<GroceryCategory, CategoryMetadata> = {
  [GroceryCategory.Produce]: {
    name: 'Produce',
    labelKey: 'groceryList.categories.produce',
    icon: '🥬',
    color: '#4CAF50',
    sortOrder: 1,
  },
  [GroceryCategory.Dairy]: {
    name: 'Dairy',
    labelKey: 'groceryList.categories.dairy',
    icon: '🥛',
    color: '#2196F3',
    sortOrder: 2,
  },
  [GroceryCategory.Meat]: {
    name: 'Meat & Seafood',
    labelKey: 'groceryList.categories.meat',
    icon: '🍖',
    color: '#F44336',
    sortOrder: 3,
  },
  [GroceryCategory.Bakery]: {
    name: 'Bakery',
    labelKey: 'groceryList.categories.bakery',
    icon: '🍞',
    color: '#FF9800',
    sortOrder: 4,
  },
  [GroceryCategory.Pantry]: {
    name: 'Pantry',
    labelKey: 'groceryList.categories.pantry',
    icon: '🥫',
    color: '#795548',
    sortOrder: 5,
  },
  [GroceryCategory.Spices]: {
    name: 'Spices',
    labelKey: 'groceryList.categories.spices',
    icon: '🌶️',
    color: '#D84315',
    sortOrder: 6,
  },
  [GroceryCategory.Frozen]: {
    name: 'Frozen',
    labelKey: 'groceryList.categories.frozen',
    icon: '🧊',
    color: '#00BCD4',
    sortOrder: 7,
  },
  [GroceryCategory.Beverages]: {
    name: 'Beverages',
    labelKey: 'groceryList.categories.beverages',
    icon: '🥤',
    color: '#9C27B0',
    sortOrder: 8,
  },
  [GroceryCategory.Other]: {
    name: 'Other',
    labelKey: 'groceryList.categories.other',
    icon: '🛒',
    color: '#757575',
    sortOrder: 9,
  },
};
